import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/design-system/button';
import { Field } from '@/design-system/field';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { Input, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import { toast } from '@/design-system/toaster';
import { updatePackage } from '@/services/api/packages';
import { getBackendErrorMessage } from '@/utils/errorHandler';

// The modal only needs identity, the editable numbers, and a display label.
export interface EditablePackage {
  id: string;
  itemName?: string;
  po?: string;
  weight?: number;
  volume?: number;
  koli?: number;
}

interface EditPackageModalProps {
  pkg: EditablePackage | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

// Mirrors the backend rule: weight must be positive, volume non-negative, both
// capped at 3 decimals (numeric(10,3)).
const isValidWeight = (n: number) => Number.isFinite(n) && n > 0;
const isValidVolume = (n: number) => Number.isFinite(n) && n >= 0;

// Same bounds the BE DTO (and the CSV import's `Koli` column) enforces:
// whole number, 1–10000.
const MAX_KOLI = 10000;
const isValidKoli = (n: number) =>
  Number.isInteger(n) && n >= 1 && n <= MAX_KOLI;

const EditPackageModal: React.FC<EditPackageModalProps> = ({
  pkg,
  open,
  onClose,
  onSaved,
}) => {
  const [weight, setWeight] = useState('');
  const [volume, setVolume] = useState('');
  const [koli, setKoli] = useState('');
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const weightRef = useRef<HTMLInputElement>(null);

  // Reset the form whenever a new package is opened for editing.
  useEffect(() => {
    if (open && pkg) {
      setWeight(String(pkg.weight ?? ''));
      setVolume(String(pkg.volume ?? 0));
      setKoli(String(pkg.koli ?? 1));
      setTouched(false);
    }
  }, [open, pkg]);

  const weightNum = Number(weight);
  const volumeNum = Number(volume);
  const koliNum = Number(koli);
  const weightOk = isValidWeight(weightNum);
  const volumeOk = isValidVolume(volumeNum);
  const koliOk = isValidKoli(koliNum);

  const handleSave = async () => {
    setTouched(true);
    if (!pkg || !weightOk || !volumeOk || !koliOk) return;
    setSaving(true);
    try {
      await updatePackage(pkg.id, {
        weight: weightNum,
        volume: volumeNum,
        koli: koliNum,
      });
      toast.success('Paket diperbarui', {
        description: 'Berat, volume & koli tersimpan.',
      });
      onSaved();
      onClose();
    } catch (err) {
      toast.error('Gagal menyimpan', {
        description: getBackendErrorMessage(err),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!pkg) return null;

  return (
    <FormModal
      open={open}
      onClose={saving ? () => undefined : onClose}
      icon={<i className="ri-edit-line" />}
      iconTone="primary"
      title="Edit package"
      subtitle={pkg.itemName || pkg.po}
      size="sm"
      closeOnBackdropClick={!saving}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <i className="ri-loader-4-line mr-1.5 animate-spin" />}
            Save changes
          </Button>
        </div>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        noValidate
      >
        <Field>
          <Label htmlFor="edit-weight" required>
            Weight (kg)
          </Label>
          <InputRoot size="md" invalid={touched && !weightOk}>
            <Input
              id="edit-weight"
              ref={weightRef}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.001"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="0.000"
            />
          </InputRoot>
          {touched && !weightOk && (
            <Hint tone="error">Berat harus lebih besar dari 0.</Hint>
          )}
        </Field>

        <Field>
          <Label htmlFor="edit-volume" required>
            Volume (m³)
          </Label>
          <InputRoot size="md" invalid={touched && !volumeOk}>
            <Input
              id="edit-volume"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.001"
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
              placeholder="0.000"
            />
          </InputRoot>
          {touched && !volumeOk && (
            <Hint tone="error">Volume tidak boleh negatif.</Hint>
          )}
        </Field>

        <Field>
          <Label htmlFor="edit-koli" required>
            Koli (dus)
          </Label>
          <InputRoot size="md" invalid={touched && !koliOk}>
            <Input
              id="edit-koli"
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_KOLI}
              step="1"
              value={koli}
              onChange={(e) => setKoli(e.target.value)}
              placeholder="1"
            />
          </InputRoot>
          {touched && !koliOk && (
            <Hint tone="error">Koli harus bilangan bulat 1–{MAX_KOLI}.</Hint>
          )}
        </Field>
      </form>
    </FormModal>
  );
};

export default EditPackageModal;
